import ImageWithFallback from './ImageWithFallback';

/**
 * The shape `services/imgproxy.responsiveImage()` puts in an API response.
 *
 * `srcSet` is null — never an empty string — when imgproxy is unavailable or
 * the object is not publicly servable. The backend is deliberate about that
 * distinction, and so is this component: a rendered `srcset=""` is a broken
 * attribute, not an absent one.
 */
export interface ResponsiveImageSource {
  url: string | null;
  srcSet?: string | null;
}

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'>;

interface ResponsiveImageProps extends ImgProps {
  /** A `{ url, srcSet }` pair from the API. Takes precedence over `src`. */
  image?: ResponsiveImageSource | null;
  /**
   * A plain URL, for callers that have no srcSet: an external stock photo, a
   * legacy row, or a tenant whose imgproxy config has not landed yet.
   */
  src?: string | null;
  /**
   * Above-the-fold. Loads eagerly at high priority instead of lazily.
   *
   * Only true for the LCP element — a hero, the first product image. Marking
   * several images priority is the same as marking none, because the point is
   * to tell the browser which one to fetch first.
   */
  priority?: boolean;
  /**
   * CSS `aspect-ratio` (e.g. `"3 / 4"`) reserving the box before the image
   * arrives. Without it a lazily-loaded image has zero height until it decodes
   * and everything below it jumps — the layout shift is caused BY the lazy
   * loading this component turns on by default, so the two belong together.
   */
  aspectRatio?: string;
  fallbackClassName?: string;
  fallbackLabel?: string;
  fallbackIcon?: boolean;
}

/**
 * An `<img>` that uses the signed imgproxy variants when the backend offered
 * any, and degrades to exactly what it would have rendered before when it did
 * not.
 *
 * ## Why this wraps ImageWithFallback rather than replacing it
 *
 * The empty/failed placeholder is real behaviour that 22 call sites depend on,
 * and duplicating it would mean two components drifting apart on what a missing
 * image looks like. This adds the three things that markup alone cannot: the
 * API pair, CLS reservation, and fetch priority.
 *
 * `sizes` is only emitted alongside a `srcSet`. On its own it does nothing but
 * mislead the next reader into thinking responsive delivery is working.
 */
export default function ResponsiveImage({
  image,
  src,
  sizes,
  priority = false,
  aspectRatio,
  style,
  ...props
}: ResponsiveImageProps) {
  const url = image?.url ?? src ?? undefined;
  const srcSet = image?.srcSet || undefined;

  return (
    <ImageWithFallback
      src={url ?? undefined}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      decoding={priority ? 'sync' : 'async'}
      style={aspectRatio ? { aspectRatio, ...style } : style}
      {...props}
    />
  );
}
